import type { AdaptiveLogger } from "@1js/office-online-jsapi-core";
import { LogCategory, TraceLevel } from "@1js/office-online-jsapi-core";
import type { AccessTokenConsumer, AccessTokenResponse } from "../types";
import { GraphUrlRegex } from "../utility";
import type {
  Action,
  ESSOptions,
  HttpHeaders,
  VroomApiInfo,
  VroomApiOptions,
} from "./types";
export function getAccessTokenTypeFromUrl(
  url: string
): AccessTokenConsumer | undefined {
  if (url && url.length) {
    if (url.match(GraphUrlRegex)) {
      return "Graph";
    }
  }
  return undefined;
}

export const getActionString = (action: Action): string => {
  return `action=${action}`;
};

export const getEarlySessionStartString = (
  essOptions?: ESSOptions
): (string | undefined)[] => {
  return essOptions?.isESSEnabled && essOptions?.wacDataCenter
    ? [
        getWacDataCenterString(essOptions?.wacDataCenter),
        getUserSessionIdString(essOptions?.sessionId),
        getHostCorrelationIdString(essOptions?.hostCorrelationId),
      ]
    : [];
};

export const getWacDataCenterString = (
  wacDataCenter?: string | null
): string | undefined => {
  return wacDataCenter ? `WacDataCenter=${wacDataCenter}` : undefined;
};

export const getUserSessionIdString = (
  userSessionId?: string
): string | undefined => {
  return userSessionId ? `usid=${userSessionId}` : undefined;
};

export const getHostCorrelationIdString = (
  hostCorrelationId?: string
): string | undefined => {
  return hostCorrelationId ? `hid=${hostCorrelationId}` : undefined;
};

export const getParamStatement = (
  params: Array<string>
): string | undefined => {
  return params && params.length ? "$select=" + params.join(",") : undefined;
};

export function getHeaders(
  tokenResponse: AccessTokenResponse,
  logger?: AdaptiveLogger
): HttpHeaders {
  const headerVals = {
    accept: "application/json",
    prefer: "allow-zerobyte",
  };
  return applyHeaderFromTokenResponse(
    tokenResponse,
    headerVals,
    undefined /* rpsCompactPrefix */,
    logger
  );
}

export function applyHeaderFromTokenResponse(
  tokenResponse: AccessTokenResponse,
  headers?: HttpHeaders,
  rpsCompactPrefix?: string,
  logger?: AdaptiveLogger
): HttpHeaders {
  let headersNormalized = headers;
  if (!tokenResponse || !tokenResponse.success) {
    return headersNormalized ?? {};
  }

  if (!headersNormalized) {
    headersNormalized = {};
  }

  /**
   * Temporarily log the token format to understand if we ever actually receive RPS (or any other) tokens
   * in OWL. It seems that we only ever deal with JWT tokens internally but add a log here to understand
   * if this check is actually necessary or just a holdover from office-start/OfficeHome logic.
   */
  logger?.ULS.sendTraceTag(
    0x1e41301a /* tag_4qta0 */,
    LogCategory.msoulscat_Wac_UnifiedUiHost,
    TraceLevel.Important,
    `Format: ${tokenResponse.format}`
  );

  const headerPrefix =
    tokenResponse.format === "RPSCompact" ? rpsCompactPrefix || "" : "Bearer ";

  // BUG: Safari does not like headers with trailing space. Have to trim it explicitly
  // because token can be empty in mock scenarios.
  headersNormalized["Authorization"] =
    `${headerPrefix}${tokenResponse.token}`.trim();
  return headersNormalized;
}

export function getApiInfo(
  itemUrl: string,
  vroomApiOptions: VroomApiOptions,
  logger?: AdaptiveLogger
): VroomApiInfo | undefined {
  const { isConsumer, oneDriveResourceId } = vroomApiOptions;

  const parsedUrl = new URL(itemUrl);
  if (!parsedUrl || !parsedUrl.protocol || !parsedUrl.host) {
    // ASSERTTAG_IGNORE_START
    logger?.ULS.sendTraceTag(
      0x2055c69b /* tag_6v201 */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Error,
      `GetApiInfo failed. [InvalidParsedUrl: ${!parsedUrl}][InvalidProtocol: ${!parsedUrl?.protocol}][Invalidhost: ${!parsedUrl?.host}]`
    );
    // ASSERTTAG_IGNORE_FINISH
    return undefined;
  }

  const resourceUrl = isConsumer
    ? oneDriveResourceId
    : `${parsedUrl.protocol}//${parsedUrl.host}`;

  const baseUrl = isConsumer
    ? getConsumerBaseUrl(vroomApiOptions)
    : getCommercialBaseUrl(resourceUrl || "", vroomApiOptions);

  const tokenType = isConsumer
    ? "None"
    : (getAccessTokenTypeFromUrl(resourceUrl || "") ?? "None");

  if (!resourceUrl || !baseUrl || !tokenType) {
    // ASSERTTAG_IGNORE_START
    logger?.ULS.sendTraceTag(
      0x2055c69c /* tag_6v202 */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Error,
      `GetApiInfo failed. [InvalidResourceUrl: ${!resourceUrl}][InvalidBaseUrl: ${!baseUrl}][InvalidTokenType: ${!tokenType}]`
    );
    // ASSERTTAG_IGNORE_FINISH
    return undefined;
  }

  return {
    baseUrl,
    tokenType,
    resourceUrl,
    hostname: parsedUrl.hostname,
  };
}

function getConsumerBaseUrl(
  vroomApiOptions: VroomApiOptions
): string | undefined {
  return vroomApiOptions.inMockMode
    ? vroomApiOptions.officeHomeApiRoot
    : vroomApiOptions.oneDriveConsumerApi;
}

function getCommercialBaseUrl(
  defaultUrl: string,
  vroomApiOptions: VroomApiOptions,
  isNotVersioned = false,
  templateLibrary = false
): string {
  const rootUrl = vroomApiOptions.inMockMode
    ? vroomApiOptions.officeHomeApiRoot
    : defaultUrl;

  if (isNotVersioned) {
    return `${rootUrl}/_api`;
  } else if (templateLibrary) {
    return `${rootUrl}/_api/v2.1`;
  }
  return `${rootUrl}/_api/v2.0`;
}
